import React, { useState } from "react";
import axios from "axios";
import { Lock, Sparkles, CheckCircle2, Loader2, LogOut, CreditCard, ShieldCheck, Award, GraduationCap } from "lucide-react";
import { Button } from "./ui/button";
import { LOGO_URL } from "../data/mock";

const API = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  { icon: ShieldCheck, text: "Tutela digitale e assistenza legale inclusa" },
  { icon: Award, text: "Tessera Socio ACDT ufficiale con numero univoco" },
  { icon: GraduationCap, text: "Accesso alla dashboard, ai materiali gratuiti e alle news" },
];

export default function PaywallScreen({ user, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const activate = async () => {
    setErr("");
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(
        `${API}/api/checkout/create-session`,
        {
          package_id: "tessera",
          origin_url: origin,
          billing: {
            nome: user.nome || "",
            cognome: user.cognome || "",
            email: user.email || "",
            telefono: user.telefono || "",
          },
        },
        { withCredentials: true }
      );
      const url = res.data?.url;
      if (!url) throw new Error("Checkout URL non ricevuto");
      window.location.href = url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(
        typeof detail === "string"
          ? detail
          : "Errore avvio pagamento. Riprova tra qualche istante."
      );
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="paywall-screen"
      className="min-h-screen bg-gradient-to-br from-[#f5f7ff] via-white to-[#eff2ff] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] px-8 py-10 text-center">
          <img
            src={LOGO_URL}
            alt="A.C.D.T."
            className="h-16 w-16 mx-auto mb-4 rounded-xl bg-white p-1.5"
          />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur">
            <Lock className="h-3.5 w-3.5 text-[#fbbf24]" />
            <span className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#fbbf24]">
              Attiva il tuo account
            </span>
          </div>
          <h1 className="mt-4 text-2xl md:text-3xl font-extrabold text-white leading-tight">
            Ciao {user.nome || "Socio"}, benvenuto in A.C.D.T.
          </h1>
          <p className="mt-3 text-slate-200/90 text-sm md:text-base leading-relaxed max-w-lg mx-auto">
            Completa il tesseramento per attivare la tua dashboard e ricevere tutti i vantaggi
            riservati ai soci.
          </p>
        </div>

        {/* Body */}
        <div className="p-8 md:p-10">
          <div className="rounded-2xl bg-gradient-to-br from-[#fef3c7] to-white border-2 border-[#f59e0b]/30 p-6 md:p-7 mb-6">
            <div className="flex items-baseline gap-3 mb-4">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#f59e0b]">
                Tessera Annuale ACDT
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-5xl font-extrabold text-[#0a1f55]">€ 50</span>
              <span className="text-sm text-slate-500">/ anno</span>
            </div>
            <ul className="space-y-2.5">
              {BENEFITS.map((b, i) => {
                const I = b.icon;
                return (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-slate-700 leading-relaxed">
                    <div className="h-6 w-6 rounded-full bg-[#f59e0b]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <I className="h-3.5 w-3.5 text-[#f59e0b]" />
                    </div>
                    <span>{b.text}</span>
                  </li>
                );
              })}
              <li className="flex items-start gap-3 text-[14px] text-slate-700 leading-relaxed">
                <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                </div>
                <span>Fattura elettronica intestata subito disponibile</span>
              </li>
            </ul>
          </div>

          {err && (
            <div
              data-testid="paywall-error"
              className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px]"
            >
              {err}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button
              onClick={activate}
              disabled={loading}
              data-testid="paywall-cta-pay"
              className="w-full h-14 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold text-base shadow-lg shadow-[#f59e0b]/20"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Reindirizzamento a Stripe…
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5 mr-2" />
                  Paga € 50 e attiva l&apos;account
                </>
              )}
            </Button>

            <button
              type="button"
              onClick={onLogout}
              data-testid="paywall-logout"
              className="text-[13px] text-slate-500 hover:text-[#0a1f55] font-semibold inline-flex items-center gap-1 justify-center py-1"
            >
              <LogOut className="h-3.5 w-3.5" />
              Esci e completa più tardi
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Pagamento SSL sicuro
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Attivazione immediata
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
